import React, { useState } from 'react';
import { ApprovedCta, BoardStateKey } from '../boardStates';
import { PORTAL_FIXTURE } from '../mock/portalFixture';
import { IconAttach, IconCard, IconChat, IconCheck, IconClipboard, IconCross, IconFile, IconId, IconSign } from '../icons';
import { usePortalCopy, usePortalUi } from '../PortalUi';
import { PortalButton, PortalStatus } from '../ui';
import { RequirementRow } from './RequirementRow';
import {
  PortalOffer,
  PortalOfferAdjustment,
  PortalOfferChange,
  PortalOfferItem,
  PortalOfferMoney,
  PortalOfferPremium,
  PortalRequirement,
  PortalStatus as PortalApplication,
  coverageFrom,
  formatOfferAmount,
  formatOfferMoney,
  formatOfferPremium,
  formatPortalDate,
  formatPremiumLabel,
  formatPremiumRecurring,
  frequencyAdjective,
  isPortalHttpError,
  offerItemLabel,
  openOfferDocument,
} from '../api/portalService';
import { agentContactLinks } from '../api/agentContact';
import { AgentContactButtons } from './AgentContactButtons';
import { ClientPortalCopyKey } from '../traductor/clientPortal';
import { RequirementsList, requirementsHeadingCount } from './RequirementsList';

type LivePanelProps = {
  application?: PortalApplication | null;
  live?: boolean;
  offer?: PortalOffer | null;
  offerLoading?: boolean;
  /** Falló el GET de la oferta: la pantalla lo dice en vez de quedar cargando. */
  offerError?: Error | null;
  /** El pedido ya terminó. Sin esto, «no arrancó» se confunde con «falló». */
  offerSettled?: boolean;
  onRetryOffer?: () => void;
  labels?: Record<string, string>;
  /**
   * OUW-186: el cliente volvió de Logalty y la firma todavía se está
   * registrando. Mientras dure, el panel espera: no se le puede volver a
   * ofrecer firmar algo que quizá ya firmó.
   */
  awaitingSignature?: boolean;
  /** CTA de 03E (confirmar/pagar, enabled o no). Solo la consume `ApprovedPanel`. */
  approvedCta?: ApprovedCta;
  /**
   * Confirmado y el link de pago todavía no llegó. El botón Pagar sigue
   * deshabilitado, con spinner, hasta que el GET lo traiga o venza el umbral.
   */
  awaitingPaymentLink?: boolean;
  /**
   * OUW-67: el cliente salió a pagar y el aviso del primer pago todavía no
   * llegó. Mientras dure, 03E no le vuelve a ofrecer «Pagar» como si nada.
   * Lo decide el tablero (la píldora usa lo mismo).
   */
  payConfirming?: boolean;
  /** Mientras espera, el último refresco agotó sus reintentos (estable entre consultas). */
  payRefreshFailed?: boolean;
  /** Handoff live a ole-web-payment (lo arma el tablero: marca + navegación). */
  onPay?: () => void;
  /** ASA lleva demasiado: el panel deja de prometer segundos (OUW-98). */
  processingStalled?: boolean;
  /**
   * Un refetch de la solicitud está en curso (tras confirmar). Distinto de
   * `offerLoading`: acá el tablero YA tiene datos y no se tapa — el spinner
   * va en la fila del botón, no en toda la pantalla.
   */
  statusRefreshing?: boolean;
  /** El refetch de la solicitud, tras agotar sus reintentos, quedó en error. */
  statusError?: Error | null;
  onRetryStatus?: () => void;
  requirements?: PortalRequirement[];
  requirementsLoading?: boolean;
  requirementsError?: Error | null;
  requirementsSettled?: boolean;
  onRetryRequirements?: () => void;
};

export function StatePanel({
  state,
  application,
  awaitingSignature,
  live,
  offer,
  offerLoading,
  offerError,
  offerSettled,
  onRetryOffer,
  labels,
  approvedCta,
  awaitingPaymentLink,
  payConfirming,
  payRefreshFailed,
  onPay,
  processingStalled,
  statusRefreshing,
  statusError,
  onRetryStatus,
  requirements,
  requirementsLoading,
  requirementsError,
  requirementsSettled,
  onRetryRequirements,
}: {
  state: BoardStateKey;
} & LivePanelProps) {
  switch (state) {
    case 'processing':
      return <ProcessingPanel stalled={processingStalled} />;
    case 'initial':
      return (
        <InitialPanel
          live={live}
          requirements={requirements}
          requirementsLoading={requirementsLoading}
          requirementsError={requirementsError}
          requirementsSettled={requirementsSettled}
          onRetryRequirements={onRetryRequirements}
        />
      );
    case 'review':
      return <ReviewPanel live={live} />;
    case 'uwreq':
      return (
        <UwReqPanel
          live={live}
          requirements={requirements}
          requirementsLoading={requirementsLoading}
          requirementsError={requirementsError}
          requirementsSettled={requirementsSettled}
          onRetryRequirements={onRetryRequirements}
        />
      );
    case 'counter':
      return (
        <CounterPanel
          application={application}
          awaitingSignature={awaitingSignature}
          offer={offer}
          live={live}
          offerLoading={offerLoading}
          offerError={offerError}
          offerSettled={offerSettled}
          onRetryOffer={onRetryOffer}
          labels={labels}
        />
      );
    case 'approved':
      return (
        <ApprovedPanel
          application={application}
          live={live}
          approvedCta={approvedCta}
          awaitingPaymentLink={awaitingPaymentLink}
          payConfirming={payConfirming}
          payRefreshFailed={payRefreshFailed}
          onPay={onPay}
          statusRefreshing={statusRefreshing}
          statusError={statusError}
          onRetryStatus={onRetryStatus}
        />
      );
    case 'denied':
      return <DeniedPanel live={live} />;
    case 'active':
      return <ActivePanel application={application} live={live} />;
    case 'cancelled':
      return <CancelledPanel application={application} live={live} />;
    default:
      return <InitialPanel live={live} />;
  }
}

/**
 * La solicitud se acaba de enviar (la firma ES el envío) y ASA todavía no
 * decidió. Sin acciones a propósito: el tablero se queda en «en proceso»
 * hasta que la máquina se mueva (OUW-98 — nunca Congrats). El refresco lo
 * maneja `BoardPage`.
 */
function ProcessingPanel({ stalled }: { stalled?: boolean }) {
  const t = usePortalCopy();
  /**
   * OUW-98: el cliente se queda acá hasta que la máquina se mueva, nunca sale
   * a `/congratulations`. Pero «es cuestión de segundos» envejece mal: pasados
   * unos minutos deja de ser cierto y hay que decirle qué esperar de verdad.
   */
  return (
    <div className="ov-state on" id="ov-processing">
      <div className="scard accent-info">
        <div className="processing-row">
          <span className="processing-spinner" aria-hidden="true" />
          <h2>{stalled ? t('processingSlowTitle') : t('processingTitle')}</h2>
        </div>
        <p>{stalled ? t('processingSlowBody') : t('processingBody')}</p>
      </div>
    </div>
  );
}

function InitialPanel({
  live,
  requirements = [],
  requirementsLoading,
  requirementsError,
  requirementsSettled,
  onRetryRequirements,
}: LivePanelProps) {
  const t = usePortalCopy();
  const { openUpload, openTarget, isDone } = usePortalUi();
  if (live) {
    const pending = requirementsHeadingCount(requirements, 'initial');
    const title = pending > 0 ? t('reqMissingCount', { count: pending }) : t('reqAllSent');
    return (
      <div className="ov-state on" id="ov-initial">
        <div className="scard accent-warn">
          <p className="eyebrow warn">{t('initialEyebrow')}</p>
          <h2>{title}</h2>
          <p>{t('initialLiveBody')}</p>
          <RequirementsList
            items={requirements}
            kind="initial"
            loading={requirementsLoading}
            error={requirementsError}
            settled={requirementsSettled}
            onRetry={onRetryRequirements}
            uploadSpec="o-upload"
            replySpec="o-uw-reply"
          />
        </div>
      </div>
    );
  }
  return (
    <div className="ov-state on" id="ov-initial">
      <div className="scard accent-warn">
        <p className="eyebrow warn">{t('initialEyebrow')}</p>
        <h2>{t('initialTitle')}</h2>
        <p>{t('initialBody')}</p>
        <div className="acts" style={{ marginTop: 16 }}>
          <PortalStatus tone="soft" variant="neutral" label={t('initialDeadline')} />
        </div>
        <RequirementRow
          done
          icon={<IconCheck />}
          title={t('incomeTitle')}
          body={t('incomeBody')}
          action={<PortalStatus tone="soft" variant="active" label={t('received')} />}
        />
        <RequirementRow
          id="req-id"
          done={isDone('req-id')}
          icon={<IconId />}
          title={t('idTitle')}
          body={t('idBody')}
          action={(
            <>
              <PortalButton size="sm" className="act-do" label={t('upload')} data-spec="o-upload" data-wired onClick={() => openUpload('req-id', t('idTitle'))} />
              <PortalStatus tone="soft" variant="active" className="act-done" label={t('received')} />
            </>
          )}
        />
        <RequirementRow
          id="req-hipaa"
          done={isDone('req-hipaa')}
          icon={<IconSign />}
          title={t('hipaaTitle')}
          body={t('hipaaBody')}
          action={(
            <>
              <PortalButton size="sm" className="act-do" label={t('sign')} data-spec="o-sign-hipaa" data-wired onClick={() => openTarget('sign', 'req-hipaa')} />
              <PortalStatus tone="soft" variant="active" className="act-done" label={t('signed')} />
            </>
          )}
        />
        <RequirementRow
          id="req-quest"
          done={isDone('req-quest')}
          icon={<IconClipboard />}
          title={t('questTitle')}
          body={t('questBody')}
          action={(
            <>
              <PortalButton size="sm" className="act-do" label={t('reply')} data-spec="o-questionnaire" data-wired onClick={() => openTarget('quest', 'req-quest')} />
              <PortalStatus tone="soft" variant="active" className="act-done" label={t('answered')} />
            </>
          )}
        />
      </div>
    </div>
  );
}

function ReviewPanel({ live }: LivePanelProps) {
  const t = usePortalCopy();
  const { openModal } = usePortalUi();
  return (
    <div className="ov-state on" id="ov-review">
      <div className="scard accent-info">
        <p className="eyebrow">{t('reviewEyebrow')}</p>
        <h2>{t('reviewTitle')}</h2>
        <p>{live ? t('reviewLiveBody') : t('reviewBody')}</p>
        {/* El ETA y el aviso por WhatsApp son del prototipo: no hay fecha
            estimada en la API ni teléfono en el DTO, y el modal confirma un
            alta que nadie registra. En vivo no se ofrecen. */}
        {live ? null : (
          <div className="acts">
            <PortalStatus variant="info" dot label={t('reviewEta')} />
            <PortalButton variant="secondary" size="sm" icon={<IconChat size={16} />} label={t('notifyCta')} data-spec="o-notify" data-wired onClick={() => openModal('notify')} />
          </div>
        )}
      </div>
      {/* Feed de actividad: fechas y nombre del fixture. El historial real de
          transiciones no está expuesto por la API (OUW-87). Hasta que exista,
          en vivo no se dibuja en vez de inventarlo. */}
      {live ? null : (
        <div className="scard">
          <h3>{t('activityTitle')}</h3>
          <ul className="feed">
            <li className="on"><b>{t('activity1')}</b><span>{t('activity1Sub')}</span></li>
            <li className="done"><b>{t('activity2')}</b><span>{t('activity2Sub')}</span></li>
            <li className="done"><b>{t('activity3')}</b><span>{t('activity3Sub')}</span></li>
          </ul>
        </div>
      )}
    </div>
  );
}

function UwReqPanel({
  live,
  requirements = [],
  requirementsLoading,
  requirementsError,
  requirementsSettled,
  onRetryRequirements,
}: LivePanelProps) {
  const t = usePortalCopy();
  const { openUpload, openTarget, isDone } = usePortalUi();
  if (live) {
    const pending = requirementsHeadingCount(requirements, 'additional');
    const title = pending > 0 ? t('reqMissingCount', { count: pending }) : t('reqAllSent');
    return (
      <div className="ov-state on" id="ov-uwreq">
        <div className="scard accent-warn">
          <p className="eyebrow warn">{t('uwEyebrow')}</p>
          <h2>{title}</h2>
          <p>{t('uwBody')}</p>
          <RequirementsList
            items={requirements}
            kind="additional"
            loading={requirementsLoading}
            error={requirementsError}
            settled={requirementsSettled}
            onRetry={onRetryRequirements}
            uploadSpec="o-uw-upload"
            replySpec="o-uw-reply"
          />
        </div>
      </div>
    );
  }
  return (
    <div className="ov-state on" id="ov-uwreq">
      <div className="scard accent-warn">
        <p className="eyebrow warn">{t('uwEyebrow')}</p>
        <h2>{t('uwTitle')}</h2>
        <p>{t('uwBody')}</p>
        <div className="acts" style={{ marginTop: 16 }}>
          <PortalStatus tone="soft" variant="neutral" label={t('uwDeadline')} />
        </div>
        <RequirementRow
          id="uw-lab"
          done={isDone('uw-lab')}
          icon={<IconFile />}
          title={t('labTitle')}
          body={(
            <>
              <p>{t('labBody')}</p>
              <div className="uw-note"><b>{t('uwNote')}</b>{t('labNote')}</div>
            </>
          )}
          action={(
            <>
              <PortalStatus tone="soft" variant="warning" className="act-do" label={t('labDue')} />
              <PortalButton size="sm" className="act-do" label={t('upload')} data-spec="o-uw-upload" data-wired onClick={() => openUpload('uw-lab', t('labTitle'))} />
              <PortalStatus tone="soft" variant="active" className="act-done" label={t('received')} />
            </>
          )}
        />
        <RequirementRow
          id="uw-med"
          done={isDone('uw-med')}
          icon={<IconChat />}
          title={t('medTitle')}
          body={(
            <>
              <p>{t('medBody')}</p>
              <div className="uw-note"><b>{t('uwNote')}</b>{t('medNote')}</div>
            </>
          )}
          action={(
            <>
              <PortalButton size="sm" className="act-do" label={t('reply')} data-spec="o-uw-reply" data-wired onClick={() => openTarget('reply', 'uw-med')} />
              <PortalStatus tone="soft" variant="active" className="act-done" label={t('answered')} />
            </>
          )}
        />
      </div>
    </div>
  );
}

/**
 * OUW-246: por qué cambió ESTA cobertura. Es el mismo texto que el cliente lee
 * en la tarjeta de esa cobertura en el endoso; acá lo tiene sin bajar el PDF,
 * que es cuando está decidiendo si acepta.
 */
function justificationOf(change: PortalOfferChange, code: string): string | undefined {
  return change.justifications?.[code];
}

function fieldTitle(
  field: string,
  t: (key: ClientPortalCopyKey, vars?: Record<string, string | number>) => string,
): string {
  if (field === 'premium') return t('chgPremium');
  if (field === 'sum_assured') return t('chgCoverage');
  if (field === 'coverages') return t('chgRiders');
  if (field === 'exclusions') return t('chgExclusions');
  return field;
}

function adjustmentLabel(
  adjustment: PortalOfferAdjustment | null | undefined,
  t: (key: ClientPortalCopyKey, vars?: Record<string, string | number>) => string,
): { text: string; up: boolean } | null {
  if (!adjustment) return null;
  const down = adjustment.direction.toLowerCase() === 'decrease';
  const percent = adjustment.unit.toLowerCase() === 'percent';
  const text = percent
    ? t(down ? 'chgDeltaDownPercent' : 'chgDeltaUpPercent', { n: adjustment.value })
    : t(down ? 'chgDeltaDownFlat' : 'chgDeltaUpFlat', {
      n: formatOfferMoney(adjustment.value, adjustment.currency),
    });
  return { text, up: !down };
}

/**
 * Sube/baja %, cuando la oferta NO trae un ajuste (ETAPA-1 tarifador: el
 * recargo de suscripción no es un ajuste sobre la prima, es un parámetro
 * del motor). Es una cuenta simple del FRONTEND sobre actual/propuesto — no
 * es el recargo, y con una oferta que también cambia coberturas mezcla las
 * dos causas. Solo dice "cuánto cambia el pago", nada más.
 */
function pricePercentDelta(
  current: PortalOfferMoney | null | undefined,
  proposed: PortalOfferMoney | null | undefined,
  t: (key: ClientPortalCopyKey, vars?: Record<string, string | number>) => string,
): { text: string; up: boolean } | null {
  if (!current || !proposed || !(current.amount > 0) || !(proposed.amount > 0)) return null;
  const pct = ((proposed.amount - current.amount) / current.amount) * 100;
  const rounded = Math.round(Math.abs(pct));
  if (rounded === 0) return null; // sin cambio real: no mostrar "sube 0%"
  const up = pct > 0;
  return { up, text: t(up ? 'chgDeltaUpPercent' : 'chgDeltaDownPercent', { n: rounded }) };
}

function riderName(item: PortalOfferItem, labels: Record<string, string>): string {
  return item.text && item.text.trim() ? item.text.trim() : labels[item.code] || item.code;
}

function riderAmountChanged(current: PortalOfferItem, proposed: PortalOfferItem): boolean {
  const next = proposed.amount ?? 0;
  const previous = current.amount ?? 0;
  // api-client histórico manda current solo con code. Sin monto vigente no
  // se puede afirmar que cambió: el portal no inventa un "Se cambia".
  if (previous <= 0 || next <= 0) return false;
  return previous !== next;
}

/**
 * OUW-246: la tabla de primas del endoso, en la pantalla donde el cliente
 * decide. Antes veía el total y el motivo de cada cambio, pero no cuánto cuesta
 * lo que le sacan o le agregan: ese número solo aparecía en el PDF, después de
 * aceptar. La nota de ASA además le habla de «la columna Prima nueva», que
 * hasta ahora no existía acá.
 *
 * Una celda vacía NO es un cero: una cobertura que se agrega no tenía prima
 * antes, y una que se quita no la tiene después. La diferencia sí usa ese
 * cero, porque es la columna que el endoso imprime y la que suma el total.
 */
function OfferPremiumTable({
  premiums,
  labels,
  currency,
  notes,
}: {
  premiums: PortalOfferPremium[];
  labels: Record<string, string>;
  currency: string;
  notes: Record<string, string>;
}) {
  const t = usePortalCopy();
  if (premiums.length === 0) return null;
  const label = (code: string): string => {
    if (code === 'base') return t('premiumTableBase');
    if (code === 'rop') return t('premiumTableRop');
    return labels[code] || code;
  };
  const cell = (amount: number | null): string =>
    amount === null ? '—' : formatOfferMoney(amount, currency);
  const diff = (amount: number | null): string => {
    if (amount === null) return '—';
    if (amount > 0) return `+${formatOfferMoney(amount, currency)}`;
    return formatOfferMoney(amount, currency);
  };

  return (
    <div className="chg premium-table" data-spec="o-offer-premiums">
      <div className="chg-top">
        <h4>{t('premiumTableTitle')}</h4>
      </div>
      <table>
        <thead>
          <tr>
            <th scope="col">{t('premiumTableCoverage')}</th>
            <th scope="col">{t('premiumTableBefore')}</th>
            <th scope="col">{t('premiumTableAfter')}</th>
            <th scope="col">{t('premiumTableDiff')}</th>
          </tr>
        </thead>
        <tbody>
          {premiums.map((row) => (
            <React.Fragment key={row.code}>
              <tr>
                <th scope="row">{label(row.code)}</th>
                <td className={row.before === null ? 'none' : undefined}>{cell(row.before)}</td>
                <td className={row.after === null ? 'none' : undefined}>{cell(row.after)}</td>
                <td className={row.difference === null ? 'none' : undefined}>{diff(row.difference)}</td>
              </tr>
              {notes[row.code] ? (
                <tr>
                  <td className="note" colSpan={4}>{notes[row.code]}</td>
                </tr>
              ) : null}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <p className="premium-table-note">{t('premiumTableNote')}</p>
    </div>
  );
}

/**
 * La moneda de la tabla de primas. Primero la que api-client sella junto al
 * desglose; después, la del cambio de prima. El default en dólares quedaba mal
 * en una oferta que solo cambia coberturas: ahí no hay cambio con dinero, y la
 * tabla se imprimía en USD aunque la póliza fuera en pesos.
 */
/** El motivo de la devolución de prima, que no tiene fila propia en los cambios. */
function premiumNotes(offer: PortalOffer): Record<string, string> {
  const notes: Record<string, string> = {};
  for (const change of offer.changes) {
    const text = change.justifications?.rop?.trim();
    if (text) notes.rop = text;
  }
  return notes;
}

function offerCurrency(offer: PortalOffer): string {
  if (offer.premiumsCurrency) return offer.premiumsCurrency;
  for (const change of offer.changes) {
    const money = change.proposed || change.current;
    if (money && money.currency.trim()) return money.currency.trim();
  }
  return 'USD';
}

function LiveChangeRow({
  change,
  labels,
}: {
  change: PortalOfferChange;
  labels: Record<string, string>;
}) {
  const t = usePortalCopy();
  const delta =
    adjustmentLabel(change.adjustment, t) ??
    (change.field === 'premium' ? pricePercentDelta(change.current, change.proposed, t) : null);
  const moneyWas = change.field === 'premium'
    ? formatOfferPremium(change.current)
    : formatOfferAmount(change.current);
  const moneyNow = change.field === 'premium'
    ? formatOfferPremium(change.proposed)
    : formatOfferAmount(change.proposed);
  const currentItems = change.currentItems || [];
  const proposedItems = change.proposedItems || [];

  return (
    <div className="chg">
      <div className="chg-top">
        <h4>{fieldTitle(change.field, t)}</h4>
        {delta ? <span className={`delta ${delta.up ? 'up' : 'down'}`}>{delta.text}</span> : null}
      </div>
      {change.field === 'coverages' ? (
        <ul className="riders">
          {proposedItems.map((item) => {
            const current = currentItems.find((row) => row.code === item.code);
            if (!current || riderAmountChanged(current, item)) return null;
            return (
              <li key={`keep-${item.code}`}>
                <span>{offerItemLabel(item, labels)}</span>
                <b className="keep">{t('riderKeep')}</b>
              </li>
            );
          })}
          {proposedItems.map((item) => {
            const current = currentItems.find((row) => row.code === item.code);
            if (!current || !riderAmountChanged(current, item)) return null;
            const was = current.amount && current.amount > 0 ? formatOfferMoney(current.amount) : '—';
            const now = item.amount && item.amount > 0 ? formatOfferMoney(item.amount) : '—';
            return (
              <li key={`change-${item.code}`} className="in">
                <span>{`${riderName(item, labels)} · ${was} → ${now}`}</span>
                <b className="change">{t('riderChange')}</b>
                {justificationOf(change, item.code) ? (
                  <p className="rider-why">{justificationOf(change, item.code)}</p>
                ) : null}
              </li>
            );
          })}
          {proposedItems
            .filter((item) => !currentItems.some((current) => current.code === item.code))
            .map((item) => (
              <li key={`add-${item.code}`} className="in">
                <span>{offerItemLabel(item, labels)}</span>
                <b className="add">{t('riderAdd')}</b>
                {justificationOf(change, item.code) ? (
                  <p className="rider-why">{justificationOf(change, item.code)}</p>
                ) : null}
              </li>
            ))}
          {currentItems
            .filter((item) => !proposedItems.some((next) => next.code === item.code))
            .map((item) => (
              <li key={`drop-${item.code}`} className="out">
                <span>{offerItemLabel(item, labels)}</span>
                <b className="drop">{t('riderDrop')}</b>
                {justificationOf(change, item.code) ? (
                  <p className="rider-why">{justificationOf(change, item.code)}</p>
                ) : null}
              </li>
            ))}
        </ul>
      ) : null}
      {change.field === 'exclusions' ? (
        <div className="chips">
          {(change.proposedItems || []).map((item) => (
            <span className="chip" key={item.code}>
              <IconCross size={14} />
              {offerItemLabel(item, labels)}
            </span>
          ))}
        </div>
      ) : null}
      {change.field === 'premium' || change.field === 'sum_assured' ? (
        <div className="ba">
          <span className="was"><em>{t('chgActual')}</em><i>{moneyWas}</i></span>
          <span className="arrow">→</span>
          <span className="now"><em>{t('chgProposed')}</em>{moneyNow}</span>
        </div>
      ) : null}
      {change.field === 'premium' && change.clientNote ? (
        <div className="uw-note" data-spec="o-offer-client-note">
          <b>{t('uwNote')}</b>
          {change.clientNote}
        </div>
      ) : null}
    </div>
  );
}

export function CounterPanel({
  application,
  awaitingSignature,
  offer,
  live,
  offerLoading,
  offerError,
  offerSettled,
  onRetryOffer,
  labels = {},
}: LivePanelProps) {
  const t = usePortalCopy();
  const { openModal } = usePortalUi();
  // OUW-181: la propuesta se arma en segundo plano al emitirse la oferta, así
  // que puede no estar todavía. Mientras no esté no se puede aceptar: nadie
  // debería aceptar algo que no pudo leer. Rechazar SÍ queda habilitado — si el
  // documento nunca saliera, bloquearlo también dejaría al cliente sin salida.
  const [openingDocument, setOpeningDocument] = useState(false);
  const [documentError, setDocumentError] = useState(false);
  const documentReady = Boolean(offer?.documentReady);

  const handleOpenDocument = async () => {
    const quoteId = application?.quoteId;
    if (!quoteId || openingDocument) return;
    setOpeningDocument(true);
    setDocumentError(false);
    try {
      await openOfferDocument(quoteId);
    } catch {
      setDocumentError(true);
    } finally {
      setOpeningDocument(false);
    }
  };

  if (live) {
    // Sin oferta hay dos situaciones distintas y antes las dos decían
    // «Cargando»: mientras el GET viaja, y cuando falló. La segunda dejaba la
    // pantalla cargando para siempre, sin error ni forma de reintentar.
    if (!offer) {
      // «Falló» es: el pedido TERMINÓ y no trajo oferta, o dio error. Antes
      // esto era `!offerLoading`, que también es cierto en el instante previo a
      // que el efecto arranque: el panel mostraba el error sin haber pedido nada.
      const failed = Boolean(offerError) || (offerSettled === true && !offerLoading);
      return (
        <div className="ov-state on" id="ov-counter">
          <div className="scard accent-warn">
            <p className="eyebrow warn">{t('counterEyebrow')}</p>
            <h2>{failed ? t('counterLoadError') : t('counterLoading')}</h2>
            {failed && onRetryOffer ? (
              <div className="acts">
                <PortalButton variant="secondary" size="sm" label={t('retry')} data-spec="o-retry-offer" data-wired onClick={onRetryOffer} />
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    const expires = offer.expiresAt ? formatPortalDate(offer.expiresAt) : '';
    return (
      <div className="ov-state on" id="ov-counter">
        <div className="scard accent-warn">
          <p className="eyebrow warn">{t('counterEyebrow')}</p>
          <h2>{t('counterTitle')}</h2>
          <p>{t('counterBody')}</p>
          {expires ? (
            <div className="acts" style={{ marginTop: 16 }}>
              <PortalStatus tone="soft" variant="warning" label={t('counterExpiresUntil', { date: expires })} />
            </div>
          ) : null}
          {offer.changes.map((change) => (
            <LiveChangeRow key={change.field} change={change} labels={labels} />
          ))}
          <OfferPremiumTable
            premiums={offer.premiums}
            labels={labels}
            currency={offerCurrency(offer)}
            notes={premiumNotes(offer)}
          />
          {awaitingSignature ? (
            // Volvió de Logalty y el veredicto todavía no llegó. Se espera con
            // los actos ESCONDIDOS: ofrecerle firmar de nuevo lo llevaría a
            // abrir una segunda transacción sobre la misma oferta, y ofrecerle
            // rechazar sería peor — puede que ya la haya firmado.
            <div className="processing-row" data-spec="o-sign-registering">
              <span className="processing-spinner" aria-hidden="true" />
              <h2>{t('signOfferRegistering')}</h2>
            </div>
          ) : (
          // OUW-186: firmar ES aceptar. El botón abre el modal de conformidad
          // del tablero; «Continuar a la firma» arranca Logalty ahí. No hay
          // una pantalla aparte, así el stepper y el panel derecho quedan detrás.
          <div className="acts">
            <PortalButton
              label={t('signOfferCta')}
              data-spec="o-accept-offer"
              data-wired
              disabled={!documentReady}
              onClick={() => openModal('accept')}
            />
            {documentReady ? (
              <PortalButton
                variant="secondary"
                label={t('viewOffer')}
                data-spec="o-offer-pdf"
                data-wired
                disabled={openingDocument}
                onClick={handleOpenDocument}
              />
            ) : (
              <PortalStatus tone="soft" variant="info" label={t('offerDocumentPreparing')} data-spec="o-offer-pdf-preparing" />
            )}
            <PortalButton variant="ghost" label={t('declineOffer')} data-spec="o-decline-offer" data-wired onClick={() => openModal('decline')} />
          </div>
          )}
          {awaitingSignature ? (
            <p className="foot-note" data-spec="o-sign-registering-note">{t('signOfferRegisteringNote')}</p>
          ) : null}
          {documentError ? (
            <p className="foot-note" data-spec="o-offer-pdf-error">{t('offerDocumentError')}</p>
          ) : null}
          <p className="foot-note">
            {expires ? t('counterFootUntil', { date: expires }) : t('counterFoot')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ov-state on" id="ov-counter">
      <div className="scard accent-warn">
        <p className="eyebrow warn">{t('counterEyebrow')}</p>
        <h2>{t('counterTitle')}</h2>
        <p>{t('counterBody')}</p>
        <div className="acts" style={{ marginTop: 16 }}>
          <PortalStatus tone="soft" variant="warning" label={t('counterExpires')} />
        </div>
        <div className="chg">
          <div className="chg-top">
            <h4>{t('chgPremium')}</h4>
            <span className="delta up">{t('chgPremiumDelta')}</span>
          </div>
          <div className="ba">
            <span className="was"><em>{t('chgWas')}</em><i>{PORTAL_FIXTURE.premiumMonthly}</i></span>
            <span className="arrow">→</span>
            <span className="now"><em>{t('chgNow')}</em>{PORTAL_FIXTURE.offerPremiumMonthly}</span>
          </div>
          <div className="uw-note"><b>{t('uwNote')}</b>{t('premiumNote')}</div>
        </div>
        <div className="chg">
          <div className="chg-top">
            <h4>{t('chgCoverage')}</h4>
            <span className="delta down">{t('chgCoverageDelta')}</span>
          </div>
          <div className="ba">
            <span className="was"><em>{t('chgWas')}</em><i>{PORTAL_FIXTURE.coverage}</i></span>
            <span className="arrow">→</span>
            <span className="now"><em>{t('chgNowM')}</em>{PORTAL_FIXTURE.offerCoverage}</span>
          </div>
          <div className="uw-note"><b>{t('uwNote')}</b>{t('coverageNote')}</div>
        </div>
        <div className="chg">
          <div className="chg-top">
            <h4>{t('chgRiders')}</h4>
            <span className="delta down">{t('chgRidersDelta')}</span>
          </div>
          <ul className="riders">
            <li><span>{t('rider1')}</span><b className="keep">{t('riderKeep')}</b></li>
            <li><span>{t('rider2')}</span><b className="keep">{t('riderKeep')}</b></li>
            <li className="out"><span>{t('rider3')}</span><b className="drop">{t('riderDrop')}</b></li>
            <li className="out"><span>{t('rider4')}</span><b className="drop">{t('riderDrop')}</b></li>
          </ul>
          <div className="uw-note"><b>{t('uwNote')}</b>{t('ridersNote')}</div>
        </div>
        <div className="chg">
          <div className="chg-top">
            <h4>{t('chgExclusions')}</h4>
            <span className="delta up">{t('chgExclusionsDelta')}</span>
          </div>
          <div className="chips">
            <span className="chip"><IconCross size={14} />{t('excl1')}</span>
            <span className="chip"><IconCross size={14} />{t('excl2')}</span>
            <span className="chip"><IconCross size={14} />{t('excl3')}</span>
          </div>
          <div className="uw-note"><b>{t('uwNote')}</b>{t('exclNote')}</div>
          <button className="attach" type="button" data-spec="o-offer-attach">
            <IconAttach />
            {t('offerAttach')}
          </button>
        </div>
        <div className="acts">
          <PortalButton label={t('signOfferCta')} data-spec="o-accept-offer" data-wired onClick={() => openModal('accept')} />
          <PortalButton variant="secondary" label={t('viewOffer')} data-spec="o-offer-pdf" />
          <PortalButton variant="ghost" label={t('declineOffer')} data-spec="o-decline-offer" data-wired onClick={() => openModal('decline')} />
        </div>
        <p className="foot-note">{t('counterFoot')}</p>
      </div>
    </div>
  );
}

function ApprovedPanel({
  application,
  live,
  approvedCta,
  awaitingPaymentLink,
  payConfirming,
  payRefreshFailed,
  onPay,
  statusRefreshing,
  statusError,
  onRetryStatus,
}: LivePanelProps) {
  const t = usePortalCopy();
  const { openPay, confirmSession, showToast } = usePortalUi();
  const [confirmBusy, setConfirmBusy] = React.useState(false);
  const coverage = live && application ? coverageFrom(application) : PORTAL_FIXTURE.coverage;
  const premium = live && application ? formatPremiumLabel(application.premium) : PORTAL_FIXTURE.premium;
  const cta = approvedCta ?? { kind: 'confirm' as const, enabled: false };
  const awaitingConfirm = live && cta.kind === 'confirm' && cta.enabled;
  const canPay = live && cta.kind === 'pay' && cta.enabled;
  /**
   * OUW-108: en fase none el próximo paso se muestra igual, disabled, con
   * la razón. Nunca los dos a la vez — `approvedCtaFromApi` elige uno.
   */
  const confirmPending = live && cta.kind === 'confirm' && !cta.enabled;
  const payPending = live && cta.kind === 'pay' && !cta.enabled;
  /**
   * Tras un click en Confirmar, el POST ya volvió (`confirmBusy` baja en el
   * finally) pero el refetch que trae el estado nuevo puede seguir en curso
   * o haber fallado (hallazgo del review de OUW-70: antes esto tapaba TODO
   * el tablero con la pantalla de carga completa). Acá el spinner vive SOLO
   * en la fila del botón — el resto de la tarjeta no se mueve.
   */
  const awaitingRefresh = awaitingConfirm && statusRefreshing;
  const refreshFailed = awaitingConfirm && Boolean(statusError) && !statusRefreshing;

  const onConfirm = async () => {
    if (!confirmSession || confirmBusy) return;
    setConfirmBusy(true);
    try {
      await confirmSession();
    } catch (error) {
      if (isPortalHttpError(error) && error.status === 409) {
        return;
      }
      showToast(t('confirmWriteError'));
    } finally {
      setConfirmBusy(false);
    }
  };

  const eyebrow = awaitingConfirm ? t('confirmEyebrow') : t('approvedEyebrow');
  const title = awaitingConfirm
    ? t('confirmTitle')
    : payConfirming
      ? t('payConfirmingTitle')
      : t('approvedTitle');
  const body = awaitingConfirm
    ? t('confirmBody')
    : payConfirming
      ? t('payConfirmingBody')
      : t('approvedBody');
  const disabledReason = confirmPending
    ? t('confirmDisabledReason')
    : payPending
      ? t('payDisabledReason')
      : null;
  const foot = disabledReason
    ? disabledReason
    : payConfirming
      ? t('payConfirmingFoot')
      : awaitingConfirm
      ? t('confirmFoot')
      : t('approvedFoot', { period: live && application ? frequencyAdjective(application.premium?.frequency) : 'mensual' });

  return (
    <div className="ov-state on" id="ov-approved">
      <div className="scard accent-ok">
        <p className="eyebrow ok">{eyebrow}</p>
        <h2>{title}</h2>
        <p>{body}</p>
        <div className="terms">
          <div><b>{t('termCoverage')}</b><span>{coverage}</span></div>
          <div><b>{t('termFirstPay')}</b><span>{premium}</span></div>
          {live ? null : (
            <div className="term-stack"><b>{t('termClass')}</b><span>Preferente<br />no fumador</span></div>
          )}
        </div>
        {live ? (
          awaitingConfirm ? (
            <div className="acts">
              {confirmBusy || awaitingRefresh ? (
                <div className="processing-row" data-spec="o-confirm-busy">
                  <span className="processing-spinner" aria-hidden="true" />
                  <span>{t('confirmWriteBusy')}</span>
                </div>
              ) : refreshFailed ? (
                <>
                  <p className="foot-note" style={{ marginTop: 0 }}>{t('confirmRefreshError')}</p>
                  <PortalButton
                    variant="secondary"
                    label={t('retry')}
                    data-spec="o-confirm-retry"
                    data-wired
                    onClick={onRetryStatus}
                  />
                </>
              ) : (
                <PortalButton
                  label={t('confirmNow')}
                  data-spec="o-confirm"
                  data-wired
                  disabled={!confirmSession}
                  onClick={() => { void onConfirm(); }}
                />
              )}
            </div>
          ) : payConfirming ? (
            <div className="acts">
              {payRefreshFailed ? (
                <>
                  <p className="foot-note" style={{ marginTop: 0 }} role="status">{t('payRefreshError')}</p>
                  <PortalButton
                    variant="secondary"
                    label={t('retry')}
                    data-spec="o-pay-retry"
                    data-wired
                    onClick={onRetryStatus}
                  />
                </>
              ) : (
                <div className="processing-row" data-spec="o-pay-confirming" role="status" aria-live="polite">
                  <span className="processing-spinner" aria-hidden="true" />
                  <span>{t('payConfirming')}</span>
                </div>
              )}
              <PortalButton variant="ghost" label={t('payResume')} data-spec="o-pay-resume" data-wired onClick={onPay} />
            </div>
          ) : canPay ? (
            <div className="acts">
              <PortalButton icon={<IconCard size={18} />} label={t('payNow', { amount: premium })} data-spec="o-pay" data-wired onClick={onPay} />
            </div>
          ) : confirmPending ? (
            <div className="acts">
              <PortalButton
                label={t('confirmNow')}
                data-spec="o-confirm"
                data-wired
                disabled
                title={t('confirmDisabledReason')}
              />
            </div>
          ) : payPending ? (
            <div className="acts">
              <PortalButton
                icon={awaitingPaymentLink
                  ? <span className="processing-spinner" aria-hidden="true" />
                  : <IconCard size={18} />}
                label={t('payNow', { amount: premium })}
                data-spec="o-pay"
                data-wired
                disabled
                aria-busy={awaitingPaymentLink ? true : undefined}
                title={t('payDisabledReason')}
              />
            </div>
          ) : null
        ) : (
          <div className="acts">
            <PortalButton icon={<IconCard size={18} />} label={t('payNow', { amount: premium })} data-spec="o-pay" data-wired onClick={() => openPay('pay', premium)} />
            <PortalButton variant="secondary" label={t('viewSignedPolicy')} data-spec="o-view-policy" />
          </div>
        )}
        <p className="foot-note">{foot}</p>
      </div>
    </div>
  );
}


function DeniedPanel({ live }: LivePanelProps) {
  const t = usePortalCopy();
  return (
    <div className="ov-state on" id="ov-denied">
      <div className="scard accent-bad">
        <p className="eyebrow danger">{t('deniedEyebrow')}</p>
        <h2>{t('deniedTitle')}</h2>
        {/* El copy del prototipo promete una carta por correo: ese mail es
            OUW-56 y todavía no existe. En vivo no se promete. */}
        <p>{live ? t('deniedLiveBody') : t('deniedBody')}</p>
        {/* «Ver la carta» y «Ver otras opciones» no tienen destino (ni onClick
            ni href): botones muertos. En vivo no se dibujan. */}
        {live ? null : (
          <div className="acts">
            <PortalButton label={t('viewLetter')} data-spec="o-letter" />
          </div>
        )}
      </div>
      <div className="scard">
        <h3>{t('nextTitle')}</h3>
        <p>{live ? t('nextLiveBody') : t('nextBody', { name: PORTAL_FIXTURE.agent.firstName })}</p>
        {live ? null : (
          <div className="acts">
            <PortalButton variant="secondary" size="sm" label={t('alternatives')} data-spec="o-alternatives" />
          </div>
        )}
      </div>
    </div>
  );
}

function ActivePanel({ application, live }: LivePanelProps) {
  const t = usePortalCopy();
  const { openPay } = usePortalUi();
  const isLive = Boolean(live && application);
  const coverage = isLive ? coverageFrom(application) : PORTAL_FIXTURE.coverage;
  const premium = isLive
    ? formatPremiumRecurring(application?.premium ?? null)
    : PORTAL_FIXTURE.premium;
  const since = isLive && application?.stateEnteredAt
    ? formatPortalDate(application.stateEnteredAt)
    : '';
  const body = !isLive
    ? t('activeBody')
    : since
      ? t('activeLiveBodySince', { date: since })
      : t('activeLiveBody');

  return (
    <div className="ov-state on" id="ov-active">
      <div className="scard accent-ok">
        <p className="eyebrow ok">{t('activeEyebrow')}</p>
        <h2>{t('activeTitle')}</h2>
        <p>{body}</p>
        <div className="terms">
          <div><b>{t('termCoverage')}</b><span>{coverage}</span></div>
          <div>
            <b>{isLive ? t('termPremium', { period: frequencyAdjective(application?.premium?.frequency) }) : t('termMonthly')}</b>
            <span>{premium}</span>
          </div>
          {isLive ? null : (
            <div><b>{t('termNextDebit')}</b><span style={{ fontSize: 15 }}>{PORTAL_FIXTURE.dates.nextDebit}</span></div>
          )}
        </div>
        {isLive ? null : (
          <div className="acts">
            <PortalButton label={t('viewPolicy')} data-spec="o-policy-doc" />
            <PortalButton variant="secondary" label={t('viewReceipt')} data-spec="o-receipt" />
            <PortalButton variant="ghost" label={t('changePayMethod')} data-spec="o-payment-method" data-wired onClick={() => openPay('method')} />
          </div>
        )}
        <p className="foot-note">{isLive ? t('activeLiveFoot') : t('activeFoot')}</p>
      </div>
    </div>
  );
}

/**
 * cancelMotiveKey (OUW-76): el código del cierre → copy. El código crudo NUNCA
 * llega al DOM. Hoy la máquina sella un solo motivo de cancelación
 * (`offer_not_accepted`); los cierres por reloj llegan con su propio detalle
 * `*_expired`, y cualquier otra cosa cae en el texto genérico.
 */
function cancelMotiveKey(statusDetail: string): 'cancelMotiveOfferNotAccepted' | 'cancelMotiveExpired' | 'cancelMotiveOther' {
  const detail = (statusDetail || '').toLowerCase();
  if (detail === 'offer_not_accepted') return 'cancelMotiveOfferNotAccepted';
  if (detail.endsWith('_expired')) return 'cancelMotiveExpired';
  return 'cancelMotiveOther';
}

function CancelledPanel({ application, live }: LivePanelProps) {
  const t = usePortalCopy();
  const isLive = Boolean(live && application);
  // La fecha del cierre es cuándo entró a `cancelled`. Si no vino, la frase se
  // dice sin fecha en vez de inventar una — el prototipo traía el 23 de
  // septiembre fijo y en vivo eso era mentira.
  const closedOn = isLive && application?.stateEnteredAt
    ? formatPortalDate(application.stateEnteredAt)
    : '';
  const motiveKey = isLive ? cancelMotiveKey(application?.statusDetail || '') : null;

  const body = () => {
    if (!isLive) return t('cancelledBody');
    if (!closedOn) return t('cancelledBodyPlain');
    return motiveKey === 'cancelMotiveOfferNotAccepted'
      ? t('cancelledBodyRejected', { date: closedOn })
      : t('cancelledBodyOn', { date: closedOn });
  };

  const subject = `Solicitud ${application?.reference || ''}`.trim();
  const agent = isLive
    ? application?.agent
    : {
        name: PORTAL_FIXTURE.agent.name,
        phone: PORTAL_FIXTURE.agent.waNumber,
        email: PORTAL_FIXTURE.agent.email,
      };
  const links = agentContactLinks(agent, subject);
  const talkName = links.talkName || (isLive ? '' : PORTAL_FIXTURE.agent.firstName);

  return (
    <div className="ov-state on" id="ov-cancelled">
      <div className="scard" style={{ borderLeft: '4px solid var(--ink300)' }}>
        <p className="eyebrow muted">{t('cancelledEyebrow')}</p>
        <h2>{t('cancelledTitle')}</h2>
        <p>{body()}</p>
        <div className="uw-note" style={{ marginTop: 20 }}>
          <b>{t('cancelMotive')}</b>
          {isLive && motiveKey ? t(motiveKey) : t('cancelMotiveBody')}
        </div>
        <div className="acts">
          <AgentContactButtons
            talkName={talkName}
            wa={links.wa}
            mail={links.mail}
            writeLabel={t('writeToAgent', { name: talkName })}
            emailLabel={t('emailAgent', { name: talkName })}
            waSpec="o-write-agent-wa"
            mailSpec="o-write-agent-mail"
          />
          <PortalButton label={t('restart')} data-spec="o-restart" />
        </div>
        <p className="foot-note">
          {t('cancelledFoot', {
            name: isLive ? t('yourAgent') : PORTAL_FIXTURE.agent.firstName,
          })}
        </p>
      </div>
    </div>
  );
}
